import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { getBirthData } from '../lib/auth';
import { generateChartInterpretations } from '../lib/interpretationGenerator';
// NatalChartVisualization removed - using single SVG wheel renderer only
import { renderWheelSvg, formatEclipticLongitude } from '../chart2/render/renderWheelSvg';
import './Charts.css';

const Charts = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Separate state: currentChart (shown on page) - single chart per birth profile enforced
  const [currentChart, setCurrentChart] = useState(null);
  const [savedCharts, setSavedCharts] = useState([]); // Kept for compatibility but should always be empty
  const [error, setError] = useState('');
  const [creatingChart, setCreatingChart] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [birthDataList, setBirthDataList] = useState([]);
  const [selectedBirthDataId, setSelectedBirthDataId] = useState(null);
  const [buildSha, setBuildSha] = useState(null);
  // Chart data state - stored immediately after calculate
  const [chartJson, setChartJson] = useState(null);
  const [selectedChartId, setSelectedChartId] = useState(null);
  
  // Robust in-flight guard using useRef to prevent React strict mode double-renders
  // This ensures exactly ONE POST request per button click
  const isGeneratingRef = useRef(false);
  const abortControllerRef = useRef(null);

  // Fetch build info on mount
  useEffect(() => {
    const fetchBuildInfo = async () => {
      try {
        const response = await fetch('/api/build');
        if (response.ok) {
          const data = await response.json();
          // Use git_commit (primary), fallback to sha for backwards compat
          const gitCommit = data.git_commit || data.sha || data.build_sha || data.shortSha || data.build?.substring(0, 7);
          if (gitCommit && gitCommit !== 'unknown') {
            setBuildSha(gitCommit);
            // Log build version at startup for verification
            console.log('[CHART_BUILD] Running version:', {
              git_commit: gitCommit,
              vercel_deployment: data.vercel_deployment || 'local',
              build_time: data.build_time || data.time || 'unknown'
            });
          } else {
            // Only show 'dev' if fetch succeeded but sha is missing/invalid
            console.warn('[BUILD] API returned but git_commit is missing/invalid, using dev');
            setBuildSha('dev');
          }
        } else {
          // Fetch failed - log error and use 'dev' fallback
          console.error('[BUILD] API failed with status:', response.status, response.statusText);
          setBuildSha('dev');
        }
      } catch (err) {
        // Network/fetch error - log and use 'dev' fallback
        console.error('[BUILD] Failed to fetch build info:', err);
        setBuildSha('dev');
      }
    };
    fetchBuildInfo();
  }, []);

  useEffect(() => {
    // IMPORTANT: This useEffect ONLY fetches saved charts from database
    // NO automatic chart generation happens here - charts are generated ONLY when user clicks "Luo natal chart"
    const fetchData = async () => {
      if (!supabase) {
        setLoading(false);
        return;
      }

      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (!currentUser) {
          setLoading(false);
          return;
        }

        setUser(currentUser);

        // Hae kaikki birth_data-rivit käyttäjälle - tiettyjä kenttiä, järjestettynä
        const { data: birthDataListResult, error: birthDataError } = await supabase
          .from('birth_data')
          .select('id, created_at, updated_at, date, time, location, timezone, birth_datetime_utc, lat, lng')
          .eq('user_id', currentUser.id)
          .order('updated_at', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false });

        if (birthDataError && process.env.NODE_ENV !== 'production') {
          console.error('Error fetching birth_data:', birthDataError);
        }

        const birthDataList = birthDataListResult || [];
        setBirthDataList(birthDataList);

        // Determine which birth data to select
        let initialSelection = null;
        
        // Priority 1: URL parameter ?birthId=
        const urlBirthId = searchParams.get('birthId');
        if (urlBirthId && birthDataList.find(bd => bd.id === urlBirthId)) {
          initialSelection = urlBirthId;
        }
        
        // Priority 2: localStorage
        if (!initialSelection) {
          const lastBirthDataId = localStorage.getItem('amm_last_birth_data_id');
          if (lastBirthDataId && birthDataList.find(bd => bd.id === lastBirthDataId)) {
            initialSelection = lastBirthDataId;
          }
        }
        
        // Priority 3: Newest record (first in list after ordering)
        if (!initialSelection && birthDataList.length > 0) {
          initialSelection = birthDataList[0].id;
        }
        
        if (initialSelection) {
          setSelectedBirthDataId(initialSelection);
          // Update localStorage and URL if needed
          localStorage.setItem('amm_last_birth_data_id', initialSelection);
          if (urlBirthId !== initialSelection) {
            window.history.replaceState({}, '', `/charts?birthId=${initialSelection}`);
          }
        }

        // Don't fetch charts here - will be fetched when selectedBirthDataId is set
        setError(''); // Clear error on success
      } catch (err) {
        setError(`Tietojen haku epäonnistui: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  // Fetch charts scoped to selected birth_data_id (GET API backfills mcLon; fallback to Supabase)
  useEffect(() => {
    const fetchChartsForBirthData = async () => {
      if (!supabase || !user || !selectedBirthDataId) {
        return;
      }

      try {
        setCurrentChart(null);
        setSavedCharts([]);

        // Prefer GET /api/chart?birth_data_id= so server can backfill angles.mcLon and return guaranteed angles
        let chart = null;
        let chartJsonFromApi = null;
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData?.session?.access_token;
          if (token) {
            const res = await fetch(`/api/chart?birth_data_id=${encodeURIComponent(selectedBirthDataId)}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              chart = data.chart || null;
              chartJsonFromApi = data.chart_json || (chart?.chart_json) || null;
              if (chartJsonFromApi?.angles) {
                console.log('[CHARTS FETCH] GET API row id:', chart?.id, 'Object.keys(chart_json.angles):', Object.keys(chartJsonFromApi.angles));
              }
            }
          }
        } catch (apiErr) {
          // Fall back to Supabase
        }

        if (!chart && !chartJsonFromApi) {
          const { data, error: fetchError } = await supabase
            .from('charts')
            .select('*, interpretations (id, title, content, interpretation_type, created_at)')
            .eq('user_id', user.id)
            .eq('birth_data_id', selectedBirthDataId)
            .order('created_at', { ascending: false });
          if (!fetchError && Array.isArray(data) && data.length > 0) {
            chart = data[0];
            chartJsonFromApi = chart.chart_json;
            const rowAngles = chart?.chart_json?.angles;
            console.log('[CHARTS FETCH] Supabase fallback row id:', chart?.id, 'Object.keys(row.chart_json.angles):', rowAngles ? Object.keys(rowAngles) : 'N/A');
          }
        }

        if (chart && chartJsonFromApi) {
          const normalized = normalizeChartJson(chartJsonFromApi);
          chart.chart_json = normalized;
          setChartJson(prev => {
            if (!prev || prev.meta?.birth_data_id !== normalized.meta?.birth_data_id) return normalized;
            if (normalized.angles?.mcLon != null) return normalized;
            if (prev.angles?.mcLon != null) return prev;
            return normalized;
          });
          setSelectedChartId(chart.id);
          setCurrentChart(chart);
          setSavedCharts([]);
        } else {
          setCurrentChart(null);
          setChartJson(null);
          setSelectedChartId(null);
          setSavedCharts([]);
        }
      } catch (err) {
        // silent
      }
    };

    fetchChartsForBirthData();
  }, [selectedBirthDataId, user, supabase]);

  // Handle birth data selection change
  const handleBirthDataChange = (newId) => {
    // Clear current chart immediately to avoid showing previous person's chart
    setCurrentChart(null);
    setChartJson(null);
    setSelectedChartId(null);
    setSavedCharts([]);
    setSelectedBirthDataId(newId);
    localStorage.setItem('amm_last_birth_data_id', newId);
    window.history.replaceState({}, '', `/charts?birthId=${newId}`);
  };

  // SINGLE render entry point - validates and renders chart ONCE
  function renderChartFromChartJson(chartJson) {
    // Strict render guard - validate ONCE before any rendering
    if (
      !chartJson ||
      !Array.isArray(chartJson.houses) ||
      chartJson.houses.length !== 12 ||
      !Array.isArray(chartJson.planets) ||
      chartJson.planets.length === 0
    ) {
      return;
    }

    // Render key: birthId, UTC time, coordinates, angles – ensures full redraw when any of these change
    const renderKey = `${selectedBirthDataId || ''}-${chartJson.meta?.used_utc_iso ?? chartJson.meta?.utcISO ?? ''}-${chartJson.meta?.lat ?? ''}-${chartJson.meta?.lng ?? ''}-${chartJson.angles?.asc?.lon ?? chartJson.angles?.ascLon ?? ''}-${chartJson.planets?.length ?? 0}-${chartJson.houses?.length ?? 0}`;
    if (typeof console !== 'undefined' && console.log) {
      console.log('[RENDER_KEY]', renderKey);
    }

    const el = document.getElementById("chart-wheel");
    if (!el) return;

    // SVG reset before render - CLEAR completely, do NOT append
    el.innerHTML = "";

    // ChartData: pass angles in both shapes so renderWheelSvg accepts either (backend contract)
    const chartData = {
      meta: chartJson.meta || {},
      angles: {
        ...(chartJson.angles || {}),
        ascLon: chartJson.angles?.ascLon ?? chartJson.angles?.asc?.lon ?? 0,
        mcLon: chartJson.angles?.mcLon ?? chartJson.angles?.mc?.lon,
      },
      planets: chartJson.planets.map(p => ({
        key: p.key,
        nameFi: p.nameFi || p.name || p.key,
        glyph: p.glyph || '○',
        lon: p.lon ?? p.longitude ?? 0,
        sign: p.sign,
        deg: p.deg ?? 0,
        min: p.min ?? 0,
        retro: p.retro
      })),
      houses: chartJson.houses.map(h => ({
        house: h.house,
        lon: h.lon ?? h.longitude ?? h.degree ?? h.cuspLon ?? 0,
        sign: h.sign,
        deg: h.deg ?? 0,
        min: h.min ?? 0
      })),
      // Aspects: ONLY if array exists and has length > 0
      aspects: (Array.isArray(chartJson.aspects) && chartJson.aspects.length > 0) ? chartJson.aspects : []
    };

    // Layer toggles: ?layers=zodiac,axes,houses,planets,aspects (default = all on)
    // Use window.location.search so it works on direct load / prod (e.g. /charts?birthId=...&layers=zodiac,axes)
    const layersFromUrl = typeof window !== 'undefined' && window.location && window.location.search
      ? new URLSearchParams(window.location.search).get('layers')
      : null;
    const layersParam = layersFromUrl ?? searchParams.get('layers') ?? 'zodiac,axes,houses,planets,aspects';
    const enabledLayers = new Set(layersParam.split(',').map(s => s.trim()).filter(Boolean));

    // RENDER SVG ONCE - this is the only render call
    // Use size 800 to match viewBox="0 0 800 800"
    // Pass buildSha for renderer version watermark and enabledLayers for layer toggles
    const svg = renderWheelSvg(chartData, { size: 800, buildSha: buildSha || 'unknown', enabledLayers });
    el.innerHTML = svg;
    
    // Validate only inside #chart-wheel; log only, never throw (SVG always renders; production safety)
    const containerSvgs = el.querySelectorAll('svg');
    const wheelSvg = el.querySelector('svg[data-wheel="true"]');
    if (containerSvgs.length !== 1 || !wheelSvg) {
      console.error('[CHART] #chart-wheel: expected 1 SVG[data-wheel="true"], got', containerSvgs.length, 'svgs, wheel:', !!wheelSvg);
    }
  }

  // Helper: Find which house a planet is in based on longitude
  const findPlanetHouse = (planetLon, houses) => {
    if (!Array.isArray(houses) || houses.length !== 12) return null;
    
    // Sort houses by house number and get cusps
    const sortedHouses = [...houses].sort((a, b) => (a.house ?? 0) - (b.house ?? 0));
    
    // Normalize planet longitude
    const normLon = ((planetLon % 360) + 360) % 360;
    
    // Find house: planet is in house N if its lon is between cusp N and cusp N+1
    for (let i = 0; i < 12; i++) {
      const cuspLon = ((sortedHouses[i].lon ?? sortedHouses[i].longitude ?? 0) % 360 + 360) % 360;
      const nextCuspLon = ((sortedHouses[(i + 1) % 12].lon ?? sortedHouses[(i + 1) % 12].longitude ?? 0) % 360 + 360) % 360;
      
      // Handle wrap-around (house 12 to house 1)
      if (nextCuspLon < cuspLon) {
        if (normLon >= cuspLon || normLon < nextCuspLon) {
          return sortedHouses[i].house;
        }
      } else {
        if (normLon >= cuspLon && normLon < nextCuspLon) {
          return sortedHouses[i].house;
        }
      }
    }
    
    // Fallback: return house 1 if not found
    return sortedHouses[0]?.house ?? 1;
  };

  // Render chart wheel when chartJson changes - SINGLE RENDER GUARANTEE
  // This is the ONLY place where rendering is triggered
  useEffect(() => {
    if (!chartJson) {
      // Clear wheel if chartJson is cleared
      const el = document.getElementById("chart-wheel");
      if (el) el.innerHTML = "";
      return;
    }
    
    // Call single render function
    renderChartFromChartJson(chartJson);
  }, [chartJson, searchParams]); // Re-render when chart or ?layers= changes

  // Format date for display: DD.MM.YYYY klo HH:MM
  const formatBirthDateTime = (birthData) => {
    if (!birthData || !birthData.date || !birthData.time) return '';
    
    const dateParts = birthData.date.split('-');
    const timeParts = birthData.time.split(':');
    if (dateParts.length !== 3 || timeParts.length < 2) return '';
    
    const day = dateParts[2];
    const month = dateParts[1];
    const year = dateParts[0];
    const hour = timeParts[0].padStart(2, '0');
    const minute = timeParts[1].padStart(2, '0');
    
    return `${day}.${month}.${year} klo ${hour}:${minute}`;
  };

  // Normalize chart_json on client side (both from API response and Supabase)
  const normalizeChartJson = (rawChartJson) => {
    if (!rawChartJson || typeof rawChartJson !== 'object') {
      return {
        planets: [],
        houses: [],
        aspects: [],
        angles: {},
        meta: {}
      };
    }
    
    const normalized = { ...rawChartJson };
    
    // Normalize houses: always array, accept multiple formats
    let housesArray = null;
    
    // Try houses first
    if (Array.isArray(normalized.houses)) {
      housesArray = normalized.houses;
    } else if (normalized.houses && typeof normalized.houses === 'object' && !Array.isArray(normalized.houses)) {
      // Object map (keys "1".."12" or "house1".."house12")
      housesArray = Object.values(normalized.houses);
    }
    
    // Try houseCusps
    if (!housesArray || housesArray.length === 0) {
      if (Array.isArray(normalized.houseCusps)) {
        housesArray = normalized.houseCusps;
      } else if (normalized.houseCusps && typeof normalized.houseCusps === 'object' && !Array.isArray(normalized.houseCusps)) {
        housesArray = Object.values(normalized.houseCusps);
      }
    }
    
    // Try cusps
    if (!housesArray || housesArray.length === 0) {
      if (Array.isArray(normalized.cusps)) {
        housesArray = normalized.cusps;
      } else if (normalized.cusps && typeof normalized.cusps === 'object' && !Array.isArray(normalized.cusps)) {
        housesArray = Object.values(normalized.cusps);
      }
    }
    
    // Default to empty array if nothing found
    if (!housesArray) {
      housesArray = [];
    }
    
    // Ensure all houses have house number and sort by house number
    housesArray = housesArray.map((house, index) => {
      if (typeof house === 'object' && house !== null) {
        // Ensure house number exists
        if (house.house === undefined || house.house === null) {
          // Try to extract from key or use index + 1
          const houseNum = house.houseNumber || house.number || (index + 1);
          return { ...house, house: houseNum };
        }
        return house;
      }
      // If house is not an object, create one
      return { house: index + 1, lon: 0, sign: 'Aries', deg: 0, min: 0 };
    });
    
    // Sort by house number
    housesArray.sort((a, b) => (a.house || 0) - (b.house || 0));
    
    normalized.houses = housesArray;
    
    // Normalize planets: always array
    if (Array.isArray(normalized.planets)) {
      // Already array, keep it
    } else if (normalized.planets && typeof normalized.planets === 'object' && !Array.isArray(normalized.planets)) {
      // Object -> convert to array
      normalized.planets = Object.values(normalized.planets);
    } else {
      // Missing/null/invalid -> empty array
      normalized.planets = [];
    }
    
    // Normalize aspects: always array
    if (Array.isArray(normalized.aspects)) {
      // Already array, keep it
    } else if (normalized.aspects && typeof normalized.aspects === 'object' && !Array.isArray(normalized.aspects)) {
      // Object -> convert to array
      normalized.aspects = Object.values(normalized.aspects);
    } else {
      // Missing/null/invalid -> empty array
      normalized.aspects = [];
    }
    
    // Ensure angles and meta are objects
    if (!normalized.angles || typeof normalized.angles !== 'object' || Array.isArray(normalized.angles)) {
      normalized.angles = normalized.angles && typeof normalized.angles === 'object' ? normalized.angles : {};
    }
    // Backfill angles: map angles.mc?.lon -> mcLon, angles.asc?.lon -> ascLon (old DB rows / API shapes)
    const a = normalized.angles;
    if (a && typeof a === 'object') {
      if ((a.mcLon == null || !Number.isFinite(Number(a.mcLon))) && (a.mc?.lon != null && Number.isFinite(Number(a.mc.lon)))) {
        a.mcLon = Number(a.mc.lon);
        if (a.icLon == null) a.icLon = (a.mcLon + 180) % 360;
      }
      if ((a.ascLon == null || !Number.isFinite(Number(a.ascLon))) && (a.asc?.lon != null && Number.isFinite(Number(a.asc.lon)))) {
        a.ascLon = Number(a.asc.lon);
        if (a.dcLon == null) a.dcLon = (a.ascLon + 180) % 360;
      }
      if (a.ascLon != null && a.dcLon == null) a.dcLon = (Number(a.ascLon) + 180) % 360;
      if (a.mcLon != null && a.icLon == null) a.icLon = (Number(a.mcLon) + 180) % 360;
    }
    if (!normalized.meta || typeof normalized.meta !== 'object' || Array.isArray(normalized.meta)) {
      normalized.meta = normalized.meta && typeof normalized.meta === 'object' ? normalized.meta : {};
    }
    
    // Guard: ensure houses array has 12 items
    if (normalized.houses.length !== 12) {
      console.error("Invalid chart: houses missing", {
        housesLength: normalized.houses.length,
        houses: normalized.houses,
        expected: 12
      });
      // Don't throw - let it fail gracefully in UI
    }
    
    return normalized;
  };

  const handleCreateChart = async () => {
    // ROBUST GUARD: Prevent multiple simultaneous chart generations
    // Check ref FIRST (most reliable), then state flags
    if (isGeneratingRef.current) {
      return;
    }
    if (creatingChart || isCalculating) {
      return;
    }
    
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    // Set ref FIRST (before any state updates) to prevent React Strict Mode double calls
    isGeneratingRef.current = true;
    // Then set state flags
    setCreatingChart(true);
    setIsCalculating(true);
    setError('');

    // START: Chart generation - ONLY triggered by user button click
    // NO automatic generation, NO current date fallback
    // Chart generation start

    try {
      // SINGLE SOURCE OF TRUTH: Get selected birth profile
      // Priority: selectedBirthDataId (from dropdown) OR URL param ?birthId=
      const urlBirthId = searchParams.get('birthId');
      const selectedBirthId = selectedBirthDataId || urlBirthId;
      
      // Validate: must have birth data records
      if (birthDataList.length === 0) {
        throw new Error('Tallenna ensin syntymätietosi.');
      }

      // Validate: must have selected birth ID
      if (!selectedBirthId) {
        throw new Error('Valitse syntymätietoprofiili.');
      }

      // Get selected birth profile - SINGLE SOURCE OF TRUTH
      const selectedBirthProfile = birthDataList.find(bd => bd.id === selectedBirthId);
      
      if (!selectedBirthProfile) {
        throw new Error('Valittua syntymätietoprofiilia ei löytynyt.');
      }

      // Validate required fields - NO FALLBACKS
      if (!selectedBirthProfile.date) {
        throw new Error('Syntymäpäivä on pakollinen kartan luomiseen.');
      }

      if (!selectedBirthProfile.time) {
        throw new Error('Syntymäaika on pakollinen kartan luomiseen.');
      }

      // Normalize time format (HH:MM or HH:MM:SS)
      let timeStr = selectedBirthProfile.time || '';
      if (!timeStr.includes(':')) {
        if (timeStr.length === 4) {
          timeStr = `${timeStr.substring(0, 2)}:${timeStr.substring(2)}`;
        } else {
          timeStr = `${timeStr}:00`;
        }
      }
      // Remove seconds if present (HH:MM:SS -> HH:MM)
      if (timeStr.split(':').length > 2) {
        const parts = timeStr.split(':');
        timeStr = `${parts[0]}:${parts[1]}`;
      }
      
      const timezone = selectedBirthProfile.timezone || 'Europe/Helsinki';
      
      // Validate required fields
      if (!selectedBirthProfile.date || !timeStr) {
        throw new Error('Syntymäpäivä tai -aika puuttuu. Tarkista syntymätietosi.');
      }

      // Build calcInput object - SINGLE SOURCE OF TRUTH for calculation
      // NO FALLBACKS, NO new Date(), NO defaults
      const calcInput = {
        date: selectedBirthProfile.date,
        time: timeStr,
        timezone: timezone,
        lat: selectedBirthProfile.lat || null,
        lng: selectedBirthProfile.lng || null,
        birthId: selectedBirthProfile.id,
      };

      // INSTRUMENTATION: Log the exact calculation input
      console.log('[CALC INPUT]', calcInput);

      // Build request payload - ALWAYS send local components, backend will convert
      // DO NOT compute UTC on client - backend handles all timezone conversion
      const requestPayload = {
        birth_date: calcInput.date,
        birth_time: calcInput.time,
        timezone: calcInput.timezone,
        lat: calcInput.lat,
        lng: calcInput.lng,
        birthId: calcInput.birthId,
        birth_data_id: calcInput.birthId,
      };

      // INSTRUMENTATION: Log exact payload before fetch
      // Sending calculation request

      // Calculate natal chart using ONLY the selected birth profile
      // SINGLE chart generation - no loops, no arrays, no multiple charts
      // Store selectedBirthProfile in function scope for use in save step
      const birthDataId = selectedBirthProfile.id;
      
      let calculatedChart;
      try {
        // Calling chart API to get calculated chart - SINGLE FETCH with abort signal
        const response = await fetch('/api/chart', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestPayload),
          signal: abortController.signal,
        });

        // Read response body exactly once
        const text = await response.text();
        
        let responseData = null;
        try {
          responseData = text ? JSON.parse(text) : null;
        } catch (parseErr) {
          responseData = { error: 'Invalid JSON response', raw: text.substring(0, 200) };
        }

        if (!response.ok) {
          const errorData = responseData || { 
            error: 'Unknown error',
            message: `Server returned ${response.status}`,
            build: null
          };
          const errorMsg = errorData.details || errorData.message || errorData.error || text || `Server error: ${response.status}`;
          const buildInfo = errorData.build ? ` (build: ${errorData.build})` : '';
          console.error(`[POST /api/chart failed] ${response.status}: ${errorMsg}${buildInfo}`);
          throw new Error(`${errorMsg}${buildInfo}`);
        }

        // Use responseData instead of reading body again
        const payload = responseData;
        if (!payload || !payload.ok) {
          const errorMsg = payload?.details || payload?.message || payload?.error || 'Kartan laskenta epäonnistui.';
          const buildInfo = payload?.build ? ` (build: ${payload.build})` : '';
          throw new Error(`${errorMsg}${buildInfo}`);
        }
        
        // Find chart_json in payload robustly
        const rawChart =
          payload.chart_json ??
          payload.chart ??
          payload.data?.chart_json ??
          payload.data?.chart ??
          payload.result?.chart_json ??
          payload.result?.chart ??
          {
            planets: payload.planets || [],
            houses: payload.houses || [],
            aspects: payload.aspects || [],
            angles: payload.angles || {},
            meta: payload.meta || {}
          };

        if (!rawChart) {
          throw new Error("Calculate response missing chart_json");
        }

        // Normalize BEFORE storing
        const chartJson = normalizeChartJson(rawChart);
        
        // Guard: ensure normalization returned a valid object
        if (!chartJson || typeof chartJson !== 'object') {
          throw new Error('Normalization returned null or invalid result');
        }
        
        
        // Guard: check houses length
        if (chartJson.houses.length !== 12) {
          console.error("Invalid chart: houses missing", {
            housesLength: chartJson.houses.length,
            houses: chartJson.houses,
            expected: 12
          });
        }
        
        // Regression guard: API contract must include angles.mcLon for newly created chart
        if (chartJson.angles?.mcLon == null || !Number.isFinite(Number(chartJson.angles.mcLon))) {
          console.error('[CHART] Regression: POST response missing angles.mcLon', { anglesKeys: chartJson.angles ? Object.keys(chartJson.angles) : [] });
        }
        // Store chart_json from POST response (preferred); do not keep rendering stale DB row
        setChartJson(chartJson);
        setSelectedChartId(payload.chart_id || payload.id || `temp-${Date.now()}`);
        
        // Also update currentChart for backward compatibility (for renderChartContent tables)
        const calculatedChartForState = {
          id: payload.chart_id || payload.id || `temp-${Date.now()}`,
          chart_json: {
            ...chartJson,
            status: 'completed',
            calculated_at: new Date().toISOString()
          },
          created_at: new Date().toISOString(),
          birth_data_id: birthDataId
        };
        setCurrentChart(calculatedChartForState);
        
        // Clear calculating state - chart is ready to render
        setIsCalculating(false);
        
        // Use canonical chart structure from API (normalized)
        const planetsArray = chartJson.planets;
        const housesArray = chartJson.houses;
        const anglesObj = chartJson.angles;
        const aspectsArray = chartJson.aspects;
        
        const canonicalChart = {
          meta: {
            ...chartJson.meta,
            birth_data_id: birthDataId,
          },
          planets: planetsArray,
          houses: housesArray,
          angles: anglesObj,
          aspects: aspectsArray,
        };

        // Also build legacy format for backward compatibility with visualization component
        // CRITICAL: Backend planet.degree is invalid (all show risingSign.degree), so compute ONLY from longitude
        const positions = {};
        if (planetsArray && Array.isArray(planetsArray)) {
          for (let idx = 0; idx < planetsArray.length; idx++) {
            const planet = planetsArray[idx];
            if (planet && planet.key) {
              const planetKey = planet.key;
              const planetName = planetKey.charAt(0).toUpperCase() + planetKey.slice(1); // sun -> Sun
              
              // Extract longitude - this is the ONLY valid source for planet position
              const lon = planet.longitude ?? planet.lon ?? planet.position ?? null;
              
              // Compute sign and degree from longitude only (ignore invalid backend planet.deg/planet.min)
              let signEng = 'Aries';
              let degreeNum = 0;
              
              if (lon != null) {
                const v = signDegFromLongitude(lon);
                // Map Finnish sign to English for component
                const finToEngMap = {
                  "Oinas": "Aries", "Härkä": "Taurus", "Kaksoset": "Gemini",
                  "Rapu": "Cancer", "Leijona": "Leo", "Neitsyt": "Virgo",
                  "Vaaka": "Libra", "Skorpioni": "Scorpio", "Jousimies": "Sagittarius",
                  "Kauris": "Capricorn", "Vesimies": "Aquarius", "Kalat": "Pisces"
                };
                signEng = finToEngMap[v.sign] || v.sign;
                // Extract numeric degree from formatted string (e.g., "24°16'" -> 24.27)
                const degreeMatch = v.degree.match(/(\d+)°(\d+)'/);
                degreeNum = degreeMatch ? parseFloat(degreeMatch[1]) + parseFloat(degreeMatch[2]) / 60 : 0;
              }
              
              positions[planetName] = {
                longitude: lon || 0,
                latitude: planet.latitude || 0,
                sign: signEng, // English sign for component translation
                degree: degreeNum, // Computed from longitude only
                zodiacSign: {
                  name: signEng,
                  degree: degreeNum,
                },
              };
            }
          }
        }

        const housesObj = {};
        if (housesArray && Array.isArray(housesArray)) {
          for (let idx = 0; idx < housesArray.length; idx++) {
            const house = housesArray[idx];
            if (house && house.house) {
              housesObj[`House ${house.house}`] = {
                longitude: house.lon || 0,
                zodiacSign: {
                  name: house.sign || 'Aries',
                  degree: house.deg + (house.min || 0) / 60,
                },
              };
            }
          }
        }

        // Build chart object with both canonical and legacy formats
        calculatedChart = {
          // Canonical structure (primary)
          ...canonicalChart,
          // Legacy format for visualization component compatibility
          sunSign: (() => {
            const sunPlanet = planetsArray.find(p => p && p.key === 'sun');
            return sunPlanet?.sign || 'Unknown';
          })(),
          moonSign: (() => {
            const moonPlanet = planetsArray.find(p => p && p.key === 'moon');
            return moonPlanet?.sign || 'Unknown';
          })(),
          risingSign: anglesObj?.asc ? {
            sign: anglesObj.asc.sign || 'Aries',
            degree: anglesObj.asc.deg + (anglesObj.asc.min || 0) / 60,
          } : null,
          positions,
          houses: housesObj,
          aspects: aspectsArray,
          calculatedAt: canonicalChart.meta.calculated_at,
          status: 'completed',
        };

        // INSTRUMENTATION: Log calculation output summary
        // Chart calculation complete
      } catch (calcError) {
        // Chart calculation error
        throw new Error(`Kartan laskenta epäonnistui: ${calcError.message}`);
      }

      // Verify calculatedChart was created from selectedBirthProfile
      if (!calculatedChart) {
        throw new Error('Chart calculation failed - no chart data returned');
      }

      // Build chart JSON for saving - use canonical structure
      // Store the FULL canonical chart payload as single source of truth
      const chartJson = {
        // Canonical structure (primary)
        meta: {
          ...calculatedChart.meta,
          birth_data_id: birthDataId,
        },
        planets: calculatedChart.planets || [],
        houses: calculatedChart.houses || [],
        angles: calculatedChart.angles || {},
        aspects: calculatedChart.aspects || [],
        // Legacy fields for backward compatibility
        sunSign: calculatedChart.sunSign,
        moonSign: calculatedChart.moonSign,
        risingSign: calculatedChart.risingSign,
        positions: calculatedChart.positions,
        houses: calculatedChart.houses, // Legacy format (object)
        aspects: calculatedChart.aspects,
        calculatedAt: calculatedChart.calculatedAt,
        status: 'completed',
      };

      // Send precomputed chart to backend for validation and saving
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Use the SAME selectedBirthProfile from calculation (single source of truth)
      // This ensures calculation and save use identical birth data
      // Re-fetch selectedBirthProfile to ensure we have the latest data
      const saveSelectedBirthProfile = birthDataList.find(bd => bd.id === selectedBirthProfile.id);
      
      if (!saveSelectedBirthProfile || !saveSelectedBirthProfile.id) {
        console.error('Missing selectedBirthProfile, aborting save');
        setError('Syntymätietoprofiili puuttuu. Valitse syntymätietoprofiili ennen kartan tallentamista.');
        throw new Error('Birth data ID is required. Please select a birth profile.');
      }

      // Build POST body EXPLICITLY (no spreading, no conditionals)
      const payload = {
        birthId: saveSelectedBirthProfile.id,
        birth_data_id: saveSelectedBirthProfile.id,
        birth: {
          id: saveSelectedBirthProfile.id,
          date: saveSelectedBirthProfile.date,
          time: saveSelectedBirthProfile.time,
          timezone: saveSelectedBirthProfile.timezone,
          lat: saveSelectedBirthProfile.lat,
          lng: saveSelectedBirthProfile.lng,
          location: saveSelectedBirthProfile.location,
        },
        chart: chartJson,
        meta: {
          ...chartJson.meta,
          birthId: saveSelectedBirthProfile.id,
        }
      };

      // Sending save request

      const saveResponse = await fetch('/api/chart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      // Read response body exactly once
      const saveText = await saveResponse.text();
      
      let saveResponseData = null;
      try {
        saveResponseData = saveText ? JSON.parse(saveText) : null;
      } catch (parseErr) {
        saveResponseData = { error: 'Invalid JSON response', raw: saveText.substring(0, 200) };
      }

      if (!saveResponse.ok) {
        const errorData = saveResponseData || { 
          error: 'Unknown error',
          message: `Server returned ${saveResponse.status}`,
        };
        const errorMsg = errorData.details || errorData.message || errorData.error || saveText || `Server error: ${saveResponse.status}`;
        console.error(`[POST /api/chart save failed] ${saveResponse.status}: ${errorMsg}`);
        throw new Error(`Kartan tallennus epäonnistui: ${errorMsg}`);
      }

      const saveResult = saveResponseData;
      if (!saveResult.ok) {
        const errorMsg = saveResult.details || saveResult.message || saveResult.error || 'Kartan tallennus epäonnistui.';
        throw new Error(errorMsg);
      }

      // Fetch the saved chart with interpretations
      const { data: newChart, error: fetchError } = await supabase
        .from('charts')
        .select(`
          *,
          interpretations (
            id,
            title,
            content,
            interpretation_type,
            created_at
          )
        `)
        .eq('id', saveResult.chart_id)
        .single();

      if (fetchError) {
        console.error('Error fetching saved chart:', fetchError);
        // Continue anyway - chart was saved
      }

      // Use newChart as the chart to set (fetched from Supabase after save)
      const chartToSet = newChart;

      // Generoi automaattiset tulkinnat
      if (chartToSet) {
        try {
          const interpretations = generateChartInterpretations(chartJson);
          
          if (interpretations.length > 0) {
            // Tallenna tulkinnat interpretations-taululle
            const interpretationInserts = interpretations.map(int => ({
              chart_id: chartToSet.id,
              user_id: user.id,
              title: int.title,
              content: int.content,
              interpretation_type: int.interpretation_type || null
            }));

            const { error: intError } = await supabase
              .from('interpretations')
              .insert(interpretationInserts);

            // Error saving interpretations (silent)
          }
        } catch (intGenError) {
          // Älä keskeytä kartan luontia, vaikka tulkintojen generointi epäonnistuisi
          // Error generating interpretations (silent)
        }

        // Päivitä lista (hae uudelleen, jotta interpretations sisältyvät)
        const { data: updatedChart } = await supabase
          .from('charts')
          .select(`
            *,
            interpretations (
              id,
              title,
              content,
              interpretation_type,
              created_at
            )
          `)
          .eq('id', chartToSet.id)
          .single();

        // Update currentChart with saved chart from Supabase (has interpretations)
        // Use updatedChart if available (has interpretations), otherwise use chartToSet
        const finalChart = updatedChart || chartToSet;
        
        // Normalize chart_json from Supabase before setting state
        if (finalChart && finalChart.chart_json) {
          finalChart.chart_json = normalizeChartJson(finalChart.chart_json);
          
          // Ensure status is set
          if (!finalChart.chart_json.status) {
            finalChart.chart_json.status = 'completed';
          }
          
          // Validate required fields after normalization
          if (!Array.isArray(finalChart.chart_json.planets) || !Array.isArray(finalChart.chart_json.houses)) {
            // Don't throw - chart was already set from calculate response
          }
        }
        
        
        // Update with saved chart (has DB id and interpretations)
        // Do NOT set chartJson here - it was already set from calculate response at line 604
        // Only update currentChart for UI display (tables/interpretations)
        // This prevents duplicate render - chartJson useEffect will not re-trigger
        setCurrentChart(finalChart);
      } else {
        setCurrentChart(null);
      }
      // Clear savedCharts since we enforce single chart per birth profile
      setSavedCharts([]);

      // Chart generation complete
    } catch (err) {
      // Chart generation failed
      // Ignore abort errors (expected when cancelling duplicate requests)
      if (err.name !== 'AbortError') {
        setError(`Kartan luonti epäonnistui: ${err.message}`);
        // Clear chart state on error
        setCurrentChart(null);
        setChartJson(null);
        setSelectedChartId(null);
      }
    } finally {
      // Always reset guard in finally block - ensure loading is cleared
      isGeneratingRef.current = false;
      setCreatingChart(false);
      setIsCalculating(false);
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  // Helper functions to compute planet sign/degree from longitude (component-level, single source of truth)
  const SIGNS_FI = ["Oinas","Härkä","Kaksoset","Rapu","Leijona","Neitsyt","Vaaka","Skorpioni","Jousimies","Kauris","Vesimies","Kalat"];
  const SIGNS_EN_TO_FI = { Aries:"Oinas", Taurus:"Härkä", Gemini:"Kaksoset", Cancer:"Rapu", Leo:"Leijona", Virgo:"Neitsyt", Libra:"Vaaka", Scorpio:"Skorpioni", Sagittarius:"Jousimies", Capricorn:"Kauris", Aquarius:"Vesimies", Pisces:"Kalat" };

  // SINGLE SOURCE OF TRUTH: Compute sign and degree from longitude
  // This is the ONLY function that should be used to derive degrees from longitude
  function longitudeToSignDegree(longitude) {
    if (longitude == null || !Number.isFinite(Number(longitude))) {
      return { signNameFi: "–", degreeInSign: 0, degreeFormatted: "–" };
    }
    
    const lon = Number(longitude);
    // Normalize to 0-360
    const normalizedLon = ((lon % 360) + 360) % 360;
    
    // Compute sign index (0-11)
    const signIndex = Math.floor(normalizedLon / 30);
    
    // Compute degree in sign (0-30)
    const degInSign = normalizedLon % 30;
    
    // Format degree with minutes
    const deg = Math.floor(degInSign);
    const min = Math.round((degInSign - deg) * 60);
    const degreeFormatted = `${deg}°${String(min).padStart(2, "0")}′`;
    
    return {
      signNameFi: SIGNS_FI[signIndex % 12],
      degreeInSign: degInSign,
      degreeFormatted: degreeFormatted,
      longitude: normalizedLon
    };
  }
  
  // Legacy helper for backward compatibility
  function normalizeLon(x){ const n=Number(x); if(!Number.isFinite(n)) return null; return ((n%360)+360)%360; }
  function toDegMinString(degInSign){ const d=Math.floor(degInSign); const m=Math.round((degInSign-d)*60); return `${d}°${String(m).padStart(2,"0")}'`; }
  function signDegFromLongitude(longitude){
    const result = longitudeToSignDegree(longitude);
    return { sign: result.signNameFi, degree: result.degreeFormatted };
  }

  function normPlanetKey(raw){
    const s = String(raw||"").trim().toLowerCase();
    const map = {
      sun:"sun", aurinko:"sun", sol:"sun",
      moon:"moon", kuu:"moon", luna:"moon",
      mercury:"mercury", merkurius:"mercury",
      venus:"venus",
      mars:"mars",
      jupiter:"jupiter",
      saturn:"saturn", saturnus:"saturn",
      uranus:"uranus",
      neptune:"neptune", neptunus:"neptune",
      pluto:"pluto",
    };
    return map[s] || s;
  }


  // Helper function to render chart content (extracted from IIFE to avoid TDZ issues in production)
  const renderChartContent = (chart) => {
    // Use chartJson state if available (from calculate), otherwise fall back to chart.chart_json
    const chartDataToRender = chartJson || chart?.chart_json;
    
    // Show loading ONLY while calculating
    if (isCalculating) {
      return <p className="chart-status">Kartta lasketaan...</p>;
    }
    
    // If not calculating and chartJson missing, show error
    if (!chartDataToRender || Object.keys(chartDataToRender).length === 0) {
      return (
        <div className="error-message">
          <p>Karttaa ei voitu piirtää: kartta puuttuu</p>
        </div>
      );
    }
    
    try {
      // Normalize chart_json before rendering
      const chartData = normalizeChartJson(chartDataToRender);
      const isCompleted = chartData.status === 'completed' || !isCalculating;
      
      // Validate required fields after normalization
      if (!Array.isArray(chartData.planets) || !Array.isArray(chartData.houses)) {
        return (
          <div className="error-message">
            <p>Karttaa ei voitu piirtää: puuttuvia kenttiä</p>
            <p style={{ fontSize: '0.8rem', color: '#999' }}>
              Planets: {Array.isArray(chartData.planets) ? 'array' : typeof chartData.planets}, 
              Houses: {Array.isArray(chartData.houses) ? 'array' : typeof chartData.houses}
            </p>
          </div>
        );
      }
    
    // Get birth data for display - find the selected birth data
    const selectedBirthData = birthDataList.find(bd => bd.id === selectedBirthDataId);
    const birthDateTimeStr = selectedBirthData ? formatBirthDateTime(selectedBirthData) : '';
    const birthLocation = selectedBirthData?.location || 'Sijainti puuttuu';
    const birthTimezone = selectedBirthData?.timezone || 'Europe/Helsinki';
    const chartCreatedDate = new Date(chart.created_at).toLocaleDateString('fi-FI');
    
    // Single source of truth: compute planet rows from chartData.planets ONLY (use p.longitude)
    const ORDER = [
      ["sun","Aurinko"],
      ["moon","Kuu"],
      ["mercury","Merkurius"],
      ["venus","Venus"],
      ["mars","Mars"],
      ["jupiter","Jupiter"],
      ["saturn","Saturnus"],
      ["uranus","Uranus"],
      ["neptune","Neptunus"],
      ["pluto","Pluto"],
    ];

    const planetsArr = Array.isArray(chartData?.planets) ? chartData.planets : [];
    const planetByKey = planetsArr.reduce((acc, p) => {
      const k = normPlanetKey(p?.key || p?.name || p?.planet || p?.id);
      if (k) acc[k] = p;
      return acc;
    }, {});

    const planetRows = ORDER.map(([k,label]) => {
      const p = planetByKey[k];
      // HARD RULE: longitude ONLY
      const lon = p?.longitude;
      const v = signDegFromLongitude(lon);
      return { key:k, label, sign:v.sign, degree:v.degree };
    });

    const missingLonCount = planetRows.filter(r => r.degree === "–").length;
    
    // Build positions object for NatalChartVisualization component compatibility
    // SAFETY: Guard against missing data to prevent ReferenceErrors
    const finToEngMap = {
      "Oinas": "Aries", "Härkä": "Taurus", "Kaksoset": "Gemini",
      "Rapu": "Cancer", "Leijona": "Leo", "Neitsyt": "Virgo",
      "Vaaka": "Libra", "Skorpioni": "Scorpio", "Jousimies": "Sagittarius",
      "Kauris": "Capricorn", "Vesimies": "Aquarius", "Kalat": "Pisces"
    };
    
    // SAFETY: Only build positions if planets array exists and is valid
    // Update chartData in place to add positions if missing
    if (!chartData.positions && planetsArr && planetsArr.length > 0) {
      chartData.positions = {};
      ORDER.forEach(([k, label]) => {
        const p = planetByKey[k];
        const lon = p?.longitude;
        if (lon != null && typeof lon === 'number') {
          const planetName = k.charAt(0).toUpperCase() + k.slice(1);
          const v = signDegFromLongitude(lon);
          const signEng = finToEngMap[v.sign] || v.sign;
          // Extract numeric degree from formatted string (e.g., "24°16'" -> 24.27)
          const degreeMatch = v.degree && typeof v.degree === 'string' ? v.degree.match(/(\d+)°(\d+)'/) : null;
          const degreeNum = degreeMatch ? parseFloat(degreeMatch[1]) + parseFloat(degreeMatch[2]) / 60 : 0;
          chartData.positions[planetName] = {
            longitude: lon,
            latitude: (p?.latitude != null) ? p.latitude : 0,
            sign: signEng,
            degree: degreeNum,
            zodiacSign: {
              name: signEng,
              degree: degreeNum,
            },
          };
        }
      });
    }
    
    // Ensure positions exists (even if empty)
    if (!chartData.positions) {
      chartData.positions = {};
    }
    
    
    return (
      <div key={chart.id} className="chart-card">
        <h3>
          {birthDateTimeStr ? (
            <>Syntymähetki: {birthDateTimeStr} — {birthLocation} ({birthTimezone})</>
          ) : (
            <>Syntymähetki: Tieto puuttuu</>
          )}
        </h3>
        <p style={{ fontSize: '0.9rem', color: '#999', marginTop: '0.5rem' }}>
          Tallennettu: {chartCreatedDate}
        </p>
        
        
        {isCompleted && chartData && chartData.sunSign && (
          <>
            <div className="chart-summary">
              <p><strong>Aurinkomerkki:</strong> {chartData.sunSign}</p>
              <p><strong>Kuumerkki:</strong> {chartData.moonSign}</p>
              {chartData.risingSign && (
                <p><strong>Nousumerkki:</strong> {
                  typeof chartData.risingSign === 'string' 
                    ? chartData.risingSign 
                    : (chartData.risingSign.sign || 'Unknown')
                } {
                  chartData.risingSign.degree 
                    ? `(${typeof chartData.risingSign.degree === 'object' 
                      ? (chartData.risingSign.degree.degree || chartData.risingSign.degree) 
                      : chartData.risingSign.degree}°)` 
                    : ''
                }</p>
              )}
              {(() => {
                const aspectsArr = Array.isArray(chartData?.aspects) ? chartData.aspects : [];
                return aspectsArr.length > 0 && (
                  <p><strong>Aspekteja:</strong> {aspectsArr.length}</p>
                );
              })()}
            </div>
            
            {/* Planet positions table - SINGLE CANONICAL TABLE */}
            {isCompleted && (() => {
              // Use chartData as single source of truth
              const wheelData = chartData;

              const ORDER = [
                ["Sun","Aurinko"],["Moon","Kuu"],["Mercury","Merkurius"],["Venus","Venus"],["Mars","Mars"],
                ["Jupiter","Jupiter"],["Saturn","Saturnus"],["Uranus","Uranus"],["Neptune","Neptunus"],["Pluto","Pluto"],
              ];

              // Get longitude from planets array (single source of truth)
              function getPlanetLongitude(planetsArr, planetKey) {
                if (!Array.isArray(planetsArr)) return null;
                const found = planetsArr.find(p => {
                  const key = p?.key || p?.name || p?.planet || '';
                  return String(key).toLowerCase() === String(planetKey).toLowerCase();
                });
                return found?.longitude ?? found?.lon ?? null;
              }

              // Build planet rows using longitudeToSignDegree helper - single source of truth
              const planetRows = ORDER.map(([k, label]) => {
                const lon = getPlanetLongitude(planetsArr, k);
                if (lon == null || !Number.isFinite(Number(lon))) {
                  return {
                    key: k,
                    label,
                    longitude: null,
                    sign: "—",
                    degree: "—",
                    degreeInSign: 0
                  };
                }
                const result = longitudeToSignDegree(lon);
                return { 
                  key: k, 
                  label, 
                  longitude: lon,
                  sign: result.signNameFi, 
                  degree: result.degreeFormatted,
                  degreeInSign: result.degreeInSign
                };
              });
              
              // Production: no debug columns

              return (
                <div className="planet-table-container" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                  <h3>Planeettojen sijainnit</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid rgba(255,255,255,0.3)' }}>Planeetta</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid rgba(255,255,255,0.3)' }}>Merkki</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid rgba(255,255,255,0.3)' }}>Aste</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planetRows.map(r => (
                        <tr key={r.key}>
                          <td style={{ padding: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{r.label}</td>
                          <td style={{ padding: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{r.sign}</td>
                          <td style={{ padding: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{r.degree}</td>
                        </tr>
                      ))}
                      {wheelData?.risingSign && (() => {
                        const ascLon = wheelData.angles?.asc?.lon;
                        const ascResult = longitudeToSignDegree(ascLon);
                        return (
                          <tr key="asc">
                            <td style={{ padding: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Nouseva (AS)</td>
                            <td style={{ padding: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{ascResult.signNameFi}</td>
                            <td style={{ padding: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{ascResult.degreeFormatted}</td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {/* NatalChartVisualization removed - single SVG wheel only */}
          </>
        )}

        {!isCompleted && isCalculating && (
          <p className="chart-status">Kartta lasketaan...</p>
        )}

        {chart.interpretations && chart.interpretations.length > 0 && (
          <div className="interpretations">
            <h4>Tulkinnat ({chart.interpretations.length}):</h4>
            <div className="interpretations-grid">
              {chart.interpretations.map((int) => (
                <div key={int.id} className="interpretation">
                  <h5>{int.title}</h5>
                  <p>{int.content}</p>
                  {int.interpretation_type && (
                    <span className="interpretation-type">{int.interpretation_type}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {isCompleted && (!chart.interpretations || chart.interpretations.length === 0) && (
          <div className="interpretations-empty">
            <p>Tulkintoja ei vielä ole. Tulkinnat generoidaan automaattisesti kun kartta luodaan.</p>
          </div>
        )}
      </div>
    );
    } catch (renderErr) {
      return (
        <div className="error-message">
          <p>Kartan renderöinti epäonnistui: {renderErr.message}</p>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="charts-page">
        <div className="container">
          <div className="loading">
            <div className="moon-loader">
              <div className="moon"></div>
            </div>
            <p style={{ marginTop: '20px', color: '#e0e0e0' }}>Ladataan karttoja...</p>
          </div>
        </div>
      </div>
    );
  }


  return (
      <div className="charts-page">
        <div className="container">
          <h1>Astrologiakartat</h1>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Birth data selector */}
          {birthDataList.length === 0 ? (
            <div className="empty-state" style={{ marginBottom: '2rem' }}>
              <p>Sinulla ei ole vielä tallennettuja syntymätietoja.</p>
              <button 
                onClick={() => navigate('/birth-data?returnTo=/charts')}
                className="btn btn-primary"
                style={{ marginTop: '1rem' }}
              >
                Lisää syntymätietosi
              </button>
            </div>
          ) : (
            <div className="birth-data-selector" style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <div style={{ flex: '1', minWidth: '300px' }}>
                  <label htmlFor="birth-data-select" style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0' }}>
                    Valitse syntymätietoprofiili:
                  </label>
                  <select
                    id="birth-data-select"
                    value={selectedBirthDataId || ''}
                    onChange={(e) => handleBirthDataChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '1rem'
                    }}
                  >
                    {birthDataList.map((bd, index) => {
                      const isNewest = index === 0;
                      const dateTimeStr = formatBirthDateTime(bd);
                      const location = bd.location || 'Sijainti puuttuu';
                      const timezone = bd.timezone || 'Europe/Helsinki';
                      const label = `${dateTimeStr} – ${location} (${timezone})${isNewest ? ' (uusin)' : ''}`;
                      return (
                        <option key={bd.id} value={bd.id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <button
                  onClick={() => navigate('/birth-data?returnTo=/charts')}
                  className="btn btn-secondary"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Lisää uusi syntymäaika
                </button>
              </div>
              
              {/* Show selected birth data info */}
              {selectedBirthDataId && (() => {
                const selectedBirthData = birthDataList.find(bd => bd.id === selectedBirthDataId);
                if (!selectedBirthData) return null;
                const dateTimeStr = formatBirthDateTime(selectedBirthData);
                const location = selectedBirthData.location || 'Sijainti puuttuu';
                return (
                  <div style={{ 
                    color: '#e0e0e0', 
                    fontSize: '0.9rem',
                    padding: '0.5rem 0',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    Valittu syntymäaika: {dateTimeStr} – {location}
                  </div>
                );
              })()}
            </div>
          )}

          {selectedBirthDataId && (() => {
            const selectedBirthData = birthDataList.find(bd => bd.id === selectedBirthDataId);
            // Only show warning if birth_time_accuracy is explicitly set to non-exact value
            const accuracy = selectedBirthData?.birth_time_accuracy;
            const shouldShowWarning = accuracy && accuracy !== 'exact' && accuracy !== null;
            return shouldShowWarning ? (
              <div className="warning-message" style={{
                background: 'rgba(255, 193, 7, 0.2)',
                border: '1px solid rgba(255, 193, 7, 0.5)',
                color: '#ffc107',
                padding: '1rem',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                <strong>Huomio:</strong> Syntymäaika ei ole tarkka. Nousumerkki ja huoneet ovat viitteellisiä.
              </div>
            ) : null;
          })()}

        <div className="charts-actions">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Additional guard: check if already loading
                if (isGeneratingRef.current || creatingChart || isCalculating) {
                  return;
                }
                handleCreateChart();
              }}
              disabled={creatingChart || isCalculating || birthDataList.length === 0 || !selectedBirthDataId}
              className="btn btn-primary"
            >
              {creatingChart || isCalculating ? 'Luodaan...' : 'Luo natal chart'}
            </button>
            {buildSha && (
              <span style={{ 
                fontSize: '0.75rem', 
                color: '#999', 
                fontFamily: 'monospace',
                padding: '0.25rem 0.5rem',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px'
              }}>
                Build: {buildSha}
              </span>
            )}
          </div>
        </div>

        {/* Chart wheel container - rendered via useEffect */}
        {/* Key ensures React fully reconstructs the SVG container when birth/profile or chart changes */}
        <div
          id="chart-wheel"
          data-chart-wheel="true"
          className="chart-wheel"
          key={`${selectedBirthDataId || ''}-${chartJson?.meta?.used_utc_iso ?? chartJson?.meta?.utcISO ?? ''}-${chartJson?.meta?.lat ?? ''}-${chartJson?.meta?.lng ?? ''}-${chartJson?.angles?.asc?.lon ?? chartJson?.angles?.ascLon ?? ''}-${chartJson?.planets?.length ?? 0}-${chartJson?.houses?.length ?? 0}`}
        ></div>

        {/* Chart Legend - glyphs + angles (Astral Moon theme) */}
        {chartJson && Array.isArray(chartJson.planets) && chartJson.planets.length > 0 && (
          <div className="chart-legend" style={{ marginTop: '1.5rem', padding: '1.25rem', background: 'rgba(255,255,255,0.06)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1rem', fontWeight: '600', color: '#e0e0e0' }}>Planeettasijainnit</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
              {chartJson.planets.map(planet => {
                const planetLon = planet.lon ?? planet.longitude ?? 0;
                const houseNum = findPlanetHouse(planetLon, chartJson.houses);
                
                // Use unified formatter: derive from ecliptic longitude (source of truth)
                const formatted = formatEclipticLongitude(planetLon);
                const degreeText = formatted.min > 0 ? `${formatted.deg}°${formatted.min}′` : `${formatted.deg}°`;
                
                // Temporary mismatch logger (dev only)
                if (typeof console !== 'undefined' && console.log) {
                  const oldDeg = Math.round(Math.max(0, Math.min(29, planet.deg ?? 0)));
                  const oldMin = Math.round(Math.max(0, Math.min(59, planet.min ?? 0)));
                  const oldText = oldMin > 0 ? `${oldDeg}°${oldMin}′` : `${oldDeg}°`;
                  if (oldText !== degreeText || oldDeg !== formatted.deg || oldMin !== formatted.min) {
                    console.log(`[LEGEND_LABEL] ${planet.key}: lon=${planetLon.toFixed(2)}°, formatted=${degreeText}, old=${oldText}, sign=${formatted.signAbbr}`);
                  }
                }
                
                const isRetrograde = planet.retrograde === true || planet.retro === true;
                const glyph = planet.glyph || '○';
                
                return (
                  <div key={planet.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#e0e0e0' }}>
                    <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{glyph}</span>
                    <span style={{ fontWeight: '500' }}>{planet.nameFi || planet.name || planet.key}</span>
                    <span style={{ color: '#b0b0b0' }}>{degreeText}</span>
                    <span style={{ color: '#b0b0b0' }}>{formatted.signAbbr}</span>
                    <span style={{ color: '#888', fontSize: '0.85rem' }}>H{houseNum}</span>
                    {isRetrograde && <span style={{ color: '#ff6b6b', fontWeight: 'bold', fontSize: '0.85rem' }}>R</span>}
                  </div>
                );
              })}
            </div>
            
            {/* Aspect symbols legend */}
            {chartJson.aspects && Array.isArray(chartJson.aspects) && chartJson.aspects.length > 0 && (
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                <h4 style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '0.95rem', fontWeight: '600', color: '#e0e0e0' }}>Aspectit</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', fontSize: '0.85rem', color: '#b0b0b0' }}>
                  <span><span style={{ color: '#888' }}>—</span> Konjunktio (0°)</span>
                  <span><span style={{ color: '#888' }}>—</span> Oppositio (180°)</span>
                  <span><span style={{ color: '#888' }}>—</span> Trini (120°)</span>
                  <span><span style={{ color: '#888' }}>—</span> Neliö (90°)</span>
                  <span><span style={{ color: '#888' }}>—</span> Sekstili (60°)</span>
                </div>
              </div>
            )}
            
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
              <h4 style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '0.95rem', fontWeight: '600', color: '#e0e0e0' }}>Kulmat</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', fontSize: '0.85rem', color: '#b0b0b0' }}>
                <span><strong>ASC</strong> – Nouseva (horisontti itä)</span>
                <span><strong>DC</strong> – Laskeva (horisontti länsi)</span>
                <span><strong>MC</strong> – Keskipäivä</span>
                <span><strong>IC</strong> – Keskiyö</span>
              </div>
            </div>
          </div>
        )}

        {/* Render interpretations and other content */}
        {chartJson ? (
          <div className="chart-visual">
            <div className="current-chart-container">
              {renderChartContent({
                id: `temp-${Date.now()}`,
                chart_json: chartJson,
                created_at: new Date().toISOString()
              })}
            </div>
          </div>
        ) : user && selectedBirthDataId && !currentChart ? (
          <div className="empty-state">
            <p>Sinulla ei ole vielä tallennettuja karttoja tälle syntymätietoprofiilille.</p>
            <p>Luo ensimmäinen karttasi napista yllä.</p>
          </div>
        ) : user && currentChart ? (
          <div className="current-chart-container">
            {renderChartContent(currentChart)}
          </div>
        ) : null}

      </div>
    </div>
  );
};

export default Charts;
